import { Box, Card, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import type { ReportOperationSummary, ReportOperationsFilters, ReportSort } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { formatMinorUnits } from '@/utils/amount';
import { useApiResource } from '@/hooks/useApiResource';
import { gatewayAccountModeTone } from '@/features/gateway-accounts/domain';
import { capitalizeValue, paymentStatusTone } from './domain';

interface OperationsListViewProps {
  filters: ReportOperationsFilters;
  onFiltersChange: (patch: Partial<ReportOperationsFilters>) => void;
}

function toggleSort(current: ReportSort | undefined, column: 'createdAt' | 'amount'): ReportSort {
  if (current === column) return `-${column}` as ReportSort;
  return column;
}

export function OperationsListView({ filters, onFiltersChange }: OperationsListViewProps) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.listReportOperations(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={8} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'tenant_not_authorized') {
      return (
        <Card>
          <AccessDenied body={error.message} />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.operations.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState
          title={translate('pay.operations.list.emptyTitle')}
          body={translate('pay.operations.list.emptyBody')}
        />
      </Card>
    );
  }

  const sort = filters.sort ?? '-createdAt';

  const columns: Array<DataGridColumn<ReportOperationSummary>> = [
    {
      key: 'tenant',
      label: translate('pay.operations.list.columnTenant'),
      render: (item) =>
        item.tenantName ? (
          <Stack spacing={0}>
            <Typography component="span" variant="body2">
              {item.tenantName}
            </Typography>
            <Box component="span" sx={(theme) => ({ fontFamily: theme.custom.fontFamilyMono, fontSize: '0.75rem', color: 'text.secondary' })}>
              {item.tenantCode}
            </Box>
          </Stack>
        ) : (
          item.tenantCode
        ),
    },
    {
      key: 'application',
      label: translate('pay.operations.list.columnApplication'),
      render: (item) => item.applicationCode,
    },
    {
      key: 'gateway',
      label: translate('pay.operations.list.columnGateway'),
      render: (item) => (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <span>{item.gateway ? capitalizeValue(item.gateway) : '—'}</span>
          {item.gatewayAccountMode === 'test' &&
            (item.presetCode ? (
              <Tooltip title={item.presetCode}>
                <span>
                  <StatusChip label={translate('pay.gatewayAccounts.mode.test')} tone={gatewayAccountModeTone('test')} />
                </span>
              </Tooltip>
            ) : (
              <StatusChip label={translate('pay.gatewayAccounts.mode.test')} tone={gatewayAccountModeTone('test')} />
            ))}
        </Stack>
      ),
    },
    {
      key: 'type',
      label: translate('pay.operations.list.columnType'),
      render: (item) => translate(`pay.operations.type.${item.type}`),
    },
    {
      key: 'status',
      label: translate('pay.operations.list.columnStatus'),
      mobileVisible: true,
      render: (item) => (
        <StatusChip label={translate(`pay.operations.status.${item.status}`)} tone={paymentStatusTone(item.status)} />
      ),
    },
    {
      key: 'amount',
      label: translate('pay.operations.list.columnAmount'),
      align: 'right',
      mono: 'value',
      mobileVisible: true,
      sortable: {
        active: sort === 'amount' || sort === '-amount',
        direction: sort === '-amount' ? 'desc' : 'asc',
        onToggle: () => onFiltersChange({ sort: toggleSort(sort, 'amount') }),
      },
      render: (item) => formatMinorUnits(item.amount, item.currency),
    },
    {
      key: 'orderReference',
      label: translate('pay.operations.list.columnOrderReference'),
      mono: 'code',
      nowrap: true,
      render: (item) => item.orderReference,
    },
    {
      key: 'createdAt',
      label: translate('pay.operations.list.columnCreatedAt'),
      sortable: {
        active: sort === 'createdAt' || sort === '-createdAt',
        direction: sort === '-createdAt' ? 'desc' : 'asc',
        onToggle: () => onFiltersChange({ sort: toggleSort(sort, 'createdAt') }),
      },
      render: (item) => new Date(item.createdAt).toLocaleString('es-ES'),
    },
    {
      key: 'updatedAt',
      label: translate('pay.operations.list.columnUpdatedAt'),
      render: (item) => (item.updatedAt ? new Date(item.updatedAt).toLocaleString('es-ES') : '—'),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={data.items}
      getRowKey={(item) => item.id}
      onRowClick={(item) => navigate(`/operations/${encodeURIComponent(item.id)}`)}
      rowAriaLabel={translate('pay.operations.list.viewDetail')}
      pagination={{
        page: data.page.page,
        pageSize: data.page.pageSize,
        totalItems: data.page.totalItems,
        rowsPerPageLabel: translate('pay.operations.list.rowsPerPage'),
        onPageChange: (page) => onFiltersChange({ page }),
        onPageSizeChange: (pageSize) => onFiltersChange({ pageSize, page: 1 }),
      }}
    />
  );
}
